import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  deleteUser,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '@/lib/firebase';
import type { UserProfile } from '@/types/invoice';
import { useToast } from '@/components/ui/use-toast';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate(),
          subscription: {
            ...data.subscription,
            currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate(),
            cancelAt: data.subscription?.cancelAt?.toDate(),
          },
        } as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Create initial user profile in Firestore
  const createUserProfile = async (user: User) => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Check if this email has download history (from previous account)
      let historicalDownloadCount = 0;
      let historicalLastDownloadDate = '';
      let historicalDailyDownloadCount = 0;
      if (user.email) {
        const emailHistoryRef = doc(db, 'emailDownloadHistory', user.email.toLowerCase());
        const emailHistoryDoc = await getDoc(emailHistoryRef);
        if (emailHistoryDoc.exists()) {
          const histData = emailHistoryDoc.data();
          historicalDownloadCount = histData.downloadCount || 0;
          historicalLastDownloadDate = histData.lastDownloadDate || '';
          historicalDailyDownloadCount = histData.dailyDownloadCount || 0;
        }
      }

      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || null,
        plan: 'free',
        downloadCount: historicalDownloadCount,
        lastDownloadDate: historicalLastDownloadDate,
        dailyDownloadCount: historicalDailyDownloadCount,
        createdAt: Timestamp.now(),
        subscription: {
          status: 'none',
        },
      });
    }
  };

  // Refresh user profile
  const refreshUserProfile = async () => {
    if (user) {
      const profile = await fetchUserProfile(user.uid);
      setUserProfile(profile);
    }
  };

  // Monitor auth state
  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setUser(user);
      if (user) {
        try {
          await createUserProfile(user);
          const profile = await fetchUserProfile(user.uid);
          if (profile?.isBlocked) {
            toast({
              title: 'Access Denied',
              description: 'Your account has been blocked. Please contact support.',
              variant: 'destructive',
            });
            await firebaseSignOut(auth);
            setUser(null);
            setUserProfile(null);
          } else {
            setUserProfile(profile);
          }
        } catch (error) {
          console.error("Error setting up user profile:", error);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await createUserProfile(result.user);
      const profile = await fetchUserProfile(result.user.uid);
      if (profile?.isBlocked) {
        toast({
          title: 'Access Denied',
          description: 'Your account has been blocked. Please contact support.',
          variant: 'destructive',
        });
        await firebaseSignOut(auth);
        throw new Error('Your account has been blocked. Please contact support.');
      }
      toast({
        title: 'Welcome!',
        description: 'You have successfully signed in.',
      });
    } catch (error: any) {
      console.error('Google sign in error:', error);
      if (error.message === 'Your account has been blocked. Please contact support.') {
        throw error;
      }
      toast({
        title: 'Sign In Failed',
        description: error.message || 'Failed to sign in with Google',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Sign in with email and password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      const profile = await fetchUserProfile(result.user.uid);
      if (profile?.isBlocked) {
        toast({
          title: 'Access Denied',
          description: 'Your account has been blocked. Please contact support.',
          variant: 'destructive',
        });
        await firebaseSignOut(auth);
        throw new Error('Your account has been blocked. Please contact support.');
      }
      
      // Check if email is verified for email/password users
      if (!result.user.emailVerified && result.user.providerData.some(p => p.providerId === 'password')) {
        toast({
          title: 'Email Not Verified',
          description: 'Please verify your email before signing in. Check your inbox for the verification link.',
          variant: 'destructive',
        });
        await firebaseSignOut(auth);
        throw new Error('Email not verified');
      }
      
      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in.',
      });
    } catch (error: any) {
      console.error('Email sign in error:', error);
      let message = 'Failed to sign in';
      if (error.message === 'Email not verified' || error.message === 'Your account has been blocked. Please contact support.') {
        throw error;
      }
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = 'Invalid email or password';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      }
      toast({
        title: 'Sign In Failed',
        description: message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Register with email and password
  const registerWithEmail = async (
    email: string,
    password: string,
    displayName: string
  ) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      
      // Send email verification
      await sendEmailVerification(result.user);
      
      await createUserProfile(result.user);
      
      // Sign out immediately after registration so they must verify
      await firebaseSignOut(auth);
      
      toast({
        title: 'Account Created!',
        description: 'Please check your email to verify your account before signing in.',
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      let message = 'Failed to create account';
      if (error.code === 'auth/email-already-in-use') {
        message = 'This email is already registered';
      } else if (error.code === 'auth/weak-password') {
        message = 'Password should be at least 6 characters';
      }
      toast({
        title: 'Registration Failed',
        description: message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      toast({
        title: 'Signed Out',
        description: 'You have been signed out successfully.',
      });
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast({
        title: 'Password Reset Email Sent',
        description: 'Check your email for instructions to reset your password.',
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: 'Error',
        description: 'Failed to send password reset email',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Resend verification email
  const resendVerificationEmail = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'No user signed in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await sendEmailVerification(user);
      toast({
        title: 'Verification Email Sent',
        description: 'Please check your email inbox and spam folder.',
      });
    } catch (error: any) {
      console.error('Resend verification error:', error);
      let message = 'Failed to send verification email';
      if (error.code === 'auth/too-many-requests') {
        message = 'Too many requests. Please try again later.';
      }
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Delete account and all data
  const deleteAccount = async () => {
    if (!user) return;

    try {
      const uid = user.uid;
      const userEmail = user.email?.toLowerCase();

      // Preserve download count in email history before deleting
      if (userEmail) {
        const userRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const downloadCount = userData.downloadCount || 0;
          const lastDownloadDate = userData.lastDownloadDate || '';
          const dailyDownloadCount = userData.dailyDownloadCount || 0;
          const emailHistoryRef = doc(db, 'emailDownloadHistory', userEmail);
          await setDoc(emailHistoryRef, {
            email: userEmail,
            downloadCount: downloadCount,
            lastDownloadDate: lastDownloadDate,
            dailyDownloadCount: dailyDownloadCount,
            lastUpdated: Timestamp.now(),
          }, { merge: true });
        }
      }

      // Delete all invoices
      const invoicesRef = collection(db, 'users', uid, 'invoices');
      const invoicesSnapshot = await getDocs(query(invoicesRef));
      const invoiceDeletes = invoicesSnapshot.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(invoiceDeletes);

      // Delete all businesses
      const businessesRef = collection(db, 'users', uid, 'businesses');
      const businessesSnapshot = await getDocs(query(businessesRef));
      const businessDeletes = businessesSnapshot.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(businessDeletes);

      // Delete all storage files
      try {
        const userStorageRef = ref(storage, `users/${uid}`);
        const storageList = await listAll(userStorageRef);
        const storageDeletes = storageList.items.map((item) => deleteObject(item));
        await Promise.all(storageDeletes);
      } catch (storageError) {
        console.error('Error deleting storage files:', storageError);
      }

      // Delete user document
      await deleteDoc(doc(db, 'users', uid));

      // Delete Firebase Auth user
      await deleteUser(user);

      toast({
        title: 'Account Deleted',
        description: 'Your account and all data have been permanently deleted.',
      });
    } catch (error: any) {
      console.error('Delete account error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete account. Please try again or contact support.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signInWithGoogle,
    signInWithEmail,
    registerWithEmail,
    signOut,
    deleteAccount,
    resetPassword,
    resendVerificationEmail,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
